/* eslint-disable @angular-eslint/no-conflicting-lifecycle */
/* eslint-disable no-underscore-dangle */
import {FocusMonitor} from '@angular/cdk/a11y';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, DoCheck,
  ElementRef, EventEmitter,
  forwardRef, Input,
  OnChanges, OnDestroy,
  OnInit, Output,
  SimpleChanges, ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {ErrorStateMatcher} from '@angular/material/core';
import {MatInput} from '@angular/material/input';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {AutonumericDirective, AutonumericUpdateTriggerEventType} from '../../directives/autonumeric.directive';
import {MessageService} from '../../services/message-service.service';
import {WithControlCheck} from '../with-control-check/with-control-check.mixin';

class CustomFieldErrorMatcher implements ErrorStateMatcher {
  constructor(private customControl: FormControl) {}
  isErrorState(): boolean {
    return this.customControl && this.customControl.touched && this.customControl.invalid;
  }
}

// tslint:disable-next-line:no-conflicting-lifecycle
@Component({
  selector: 'app-input-form',
  templateUrl: './app-input-form.component.html',
  styleUrls: ['./app-input-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppInputFormComponent),
      multi: true
    }
  ]
})
export class AppInputFormComponent
  extends WithControlCheck(function() {
    return this.control;
  })
  implements ControlValueAccessor, OnChanges, OnInit, AfterViewInit, DoCheck, OnDestroy
{
  @ViewChild(AutonumericDirective) input: AutonumericDirective;
  @ViewChild(MatInput) matInput: ElementRef<HTMLInputElement>;
  @Input() public placeholder: string;
  @Input() public inputType: string;
  @Input() public testId: string;
  @Input() public hideLabel?: boolean;
  @Input() public showLabel?: boolean;
  @Input() public inputLabel?: string;
  @Input() public inputHint?: string;
  @Input() public inputHintIcon?: boolean;
  @Input() public inputClass?: string;
  @Input() public inputHintClass?: string;
  @Input() public warningMessage?: string;
  @Input() public isWarningVisible?: boolean;
  @Input() public shouldShowErrorMessage = true;
  @Input() public numeric = false;
  @Input() public decimals = 0;
  // tslint:disable-next-line:no-input-rename
  @Input('formControl') public control: FormControl;
  @Input() public isTouched: boolean;
  @Input() public autoFocus?: boolean;

  @Input()
  get isRequired(): boolean {
    return this._required;
  }
  set isRequired(value: boolean) {
    this._required = value;
  }
  @Input()
  get readonly(): boolean {
    return this._readonly;
  }
  set readonly(value: boolean) {
    this._readonly = value;
  }

  @Input()
  get maxLength(): number {
    return this._maxLength || null;
  }
  set maxLength(value: number) {
    this._maxLength = value;
  }

  @Output() public blurChange = new EventEmitter<FocusEvent>();
  /**
   * By default we have based our autonumeric update tracking by it's raw value update event, which is not correct
   * for the case when we update autonumeric value from outside the component (by form.patchValue, ...)
   * In order to skip extra model update event please use 'change' that only covers the cases when update came from user input.
   */
  @Input() autonumericUpdateTriggerEventType: AutonumericUpdateTriggerEventType = 'raw';

  // tslint:disable-next-line:variable-name
  private _required: boolean;
  private _readonly: boolean;
  private _maxLength: number;
  private ngUnsubscribe = new Subject<void>();

  public focused = false;

  public currencyControlOptions = {
    digitGroupSeparator: '',
    decimalCharacter: '.',
    decimalCharacterAlternative: '.',
    currencySymbol: '',
    currencySymbolPlacement: 's',
    roundingMethod: 'U',
    minimumValue: '0',
    maximumValue: undefined,
    decimalPlaces: 0,
    decimalPlacesShownOnBlur: 0,
    watchExternalChanges: true
  };
  private oldTouched = null;
  private oldValue = null;

  onChange: any = () => {};
  onTouched: any = () => {};

  constructor(
    private messageService: MessageService,
    private cd: ChangeDetectorRef,
    private fm: FocusMonitor,
    private elRef: ElementRef<HTMLElement>
  ) {
    super();
    fm.monitor(elRef.nativeElement, true)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(origin => {
        this.focused = !!origin;
        this.cd.markForCheck();
      });
  }

  ngAfterViewInit() {
    if (this.autoFocus) {
      setTimeout(() => {
        this.fm.focusVia(this.matInput, 'program');
        this.fm.stopMonitoring(this.elRef.nativeElement);
      }, 0);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.decimals) {
      this.currencyControlOptions.decimalPlaces = this.decimals;
      this.currencyControlOptions.decimalPlacesShownOnBlur = this.decimals;
    }

    if (this.numeric && this._maxLength) {
      this.currencyControlOptions.maximumValue = this.currencyControlOptions.decimalPlaces
        ? '9'.repeat(this._maxLength - this.currencyControlOptions.decimalPlaces - 1) +
          '.' +
          '9'.repeat(this.currencyControlOptions.decimalPlaces)
        : '9'.repeat(this._maxLength);
    } else {
      this.currencyControlOptions.maximumValue = undefined;
    }

    if (changes.control && this.input) {
      if (this.control && this.control.value) {
        this.input.instance.set(this.control.value);
      } else {
        this.input.instance.clear();
      }
    }
  }

  ngOnInit() {
    this.control?.statusChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.cd.markForCheck();
    });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.fm.stopMonitoring(this.elRef.nativeElement);
  }

  ngDoCheck() {
    if (this.control.touched !== this.oldTouched) {
      this.oldTouched = this.control.touched;
      this.cd.markForCheck();
    }
    if (this.numeric && this.control.value !== this.oldValue) {
      if (!this.control.value) {
        this.input?.instance.clear();
      }
      this.oldValue = this.control.value;
    }
    const hasControlChange = this.checkControlChanges();
    if (hasControlChange) {
      this.cd.markForCheck();
    }
  }

  public errorMatcher() {
    return new CustomFieldErrorMatcher(this.control);
  }

  public getErrorMessage(): string {
    if (this.control && this.control.errors) {
      return this.messageService.getControlErrorMessage(this.control.errors);
    }
    return null;
  }

  writeValue(value: any): void {}

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
}
